import {
  getPageHeader,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PageHeaderActionSecondary,
  PageHeaderActions,
  EvaluationAnalysisView,
  CLIENT_LOCALE,
} from '@yeshuman/sdk'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, FileText, HeartPulse, Link2, Loader2, Pencil } from 'lucide-react'
import { HealthListPageSkeleton } from './health-list-page-skeleton'
import {
  useCondition,
  useConditionRelations,
  useUpdateCondition,
  type ConditionRelationItem,
  type UpdateConditionData,
} from '../hooks/use-conditions'
import { useConditionReport } from '../hooks/use-condition-report'
import { CONDITION_STATUS_LABELS, conditionStatusBadgeVariant } from './condition-card'
import { ConditionFormDialog } from './condition-form-dialog'
import { HealthDisclaimer } from './health-disclaimer'

const REPORT_COPY = {
  scoreSuffix: 'self-management coverage',
  highlightsTitle: "What's going well",
  redFlagsTitle: 'Discuss with your practitioner',
}

const TARGET_TYPE_LABELS: Record<string, string> = {
  symptom: 'Symptom',
  therapeutic: 'Therapeutic',
  measurement: 'Measurement',
  activity: 'Activity',
  healthrecord: 'Health record',
}

const TARGET_TYPE_PATHS: Record<string, string> = {
  symptom: '/symptoms',
  therapeutic: '/therapeutics',
  measurement: '/measurements',
  activity: '/activity',
  healthrecord: '/records',
}

function relationTargetPath(rel: ConditionRelationItem): string {
  if (rel.target_type === 'healthrecord') return `/records/${rel.target_id}`
  return TARGET_TYPE_PATHS[rel.target_type] ?? '/patient'
}

function formatDate(iso?: string | null): string | null {
  if (!iso) return null
  try {
    return new Date(iso).toLocaleDateString(CLIENT_LOCALE, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}

export function ConditionDetailPage() {
  const { conditionId } = useParams<{ conditionId: string }>()
  const navigate = useNavigate()
  const [editOpen, setEditOpen] = useState(false)

  const { condition, isLoading, error } = useCondition(conditionId)
  const { relations, isLoading: relationsLoading } = useConditionRelations(conditionId)
  const updateC = useUpdateCondition()
  const {
    report,
    isLoading: reportLoading,
    generateReport,
    isGenerating,
    generateError,
  } = useConditionReport(conditionId)

  const { title, description } = getPageHeader(
    '/conditions',
    'Conditions',
    "Conditions you're managing, in your own words — self-declared, not diagnoses.",
  )

  if (isLoading) {
    return (
      <PageContainer>
        <HealthListPageSkeleton showFilters={false} ariaLabel="Loading condition" />
        </PageContainer>
    )
  }

  if (error || !condition) {
    return (
      <PageContainer>
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">
              {error instanceof Error ? error.message : 'Condition not found'}
            </p>
            <div className="mt-4">
              <Button variant="outline" onClick={() => navigate('/conditions')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to conditions
              </Button>
            </div>
          </CardContent>
        </Card>
        </PageContainer>
    )
  }

  const onset = formatDate(condition.onset_date)
  const abatement = formatDate(condition.abatement_date)

  const onUpdate = async (data: UpdateConditionData) => {
    await updateC.mutateAsync(data)
  }

  return (
    <PageContainer>
      <PageHeader
          leading={<PageHeaderLeading />}
          title={condition.name}
          description={description ?? title}
          icon={<HeartPulse className="h-5 w-5" />}
          actions={
            <PageHeaderActions
              secondary={
                <>
                  <PageHeaderActionSecondary onClick={() => navigate('/conditions')}>
                    <ArrowLeft className="h-4 w-4" aria-hidden />
                    All conditions
                  </PageHeaderActionSecondary>
                  <PageHeaderActionSecondary onClick={() => setEditOpen(true)}>
                    <Pencil className="h-4 w-4" aria-hidden />
                    Edit
                  </PageHeaderActionSecondary>
                </>
              }
            />
          }
        />

        <HealthDisclaimer />

        <Card>
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2">
              {condition.name}
              <Badge variant={conditionStatusBadgeVariant(condition.clinical_status)}>
                {CONDITION_STATUS_LABELS[condition.clinical_status] ?? condition.clinical_status}
              </Badge>
              <Badge variant="outline">Self-declared</Badge>
            </CardTitle>
            <CardDescription>
              {onset ? `Since ${onset}` : 'Onset not recorded'}
              {abatement ? ` · Resolved ${abatement}` : ''}
            </CardDescription>
          </CardHeader>
          {condition.notes ? (
            <CardContent>
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">{condition.notes}</p>
            </CardContent>
          ) : null}
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="h-4 w-4" aria-hidden />
              Linked health data
            </CardTitle>
            <CardDescription>
              Symptoms, therapeutics, measurements, activities, and records connected to this
              condition. Ask Lumie to link records as you log them.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {relationsLoading ? (
              <p className="text-sm text-muted-foreground">Loading links…</p>
            ) : relations.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nothing linked yet. As you log symptoms, therapeutics, or measurements, ask Lumie to
                connect them to this condition.
              </p>
            ) : (
              <ul className="space-y-2">
                {relations.map((rel) => (
                  <li
                    key={rel.id}
                    className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2"
                  >
                    <Badge variant="secondary">
                      {TARGET_TYPE_LABELS[rel.target_type] ?? rel.target_type}
                    </Badge>
                    <Link
                      to={relationTargetPath(rel)}
                      className="min-w-0 flex-1 truncate text-sm hover:underline"
                    >
                      {rel.target_summary || `${rel.target_type} ${rel.target_id}`}
                    </Link>
                    <span className="text-xs text-muted-foreground">{rel.relation_type}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4" aria-hidden />
              Self-management report
            </CardTitle>
            <CardDescription>
              Organises your own logged data against a self-management rubric. Descriptive, not a
              diagnosis or treatment advice.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => generateReport()} disabled={isGenerating}>
                {isGenerating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <FileText className="mr-2 h-4 w-4" aria-hidden />
                )}
                {report ? 'Regenerate report' : 'Generate report'}
              </Button>
              {report ? (
                <span className="text-xs text-muted-foreground">
                  Last generated {formatDate(report.created_at)}
                </span>
              ) : null}
            </div>
            {generateError ? (
              <p className="text-sm text-destructive">
                {generateError instanceof Error
                  ? generateError.message
                  : 'Failed to generate report'}
              </p>
            ) : null}
            {reportLoading ? (
              <p className="text-sm text-muted-foreground">Checking for reports…</p>
            ) : report ? (
              <EvaluationAnalysisView
                evaluationId={report.evaluation_id}
                subjectLabel={condition.name}
                copy={REPORT_COPY}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                No report yet. Generate one here, or ask Lumie in chat for a report on this
                condition.
              </p>
            )}
          </CardContent>
        </Card>

        <ConditionFormDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          condition={condition}
          onCreate={async () => {}}
          onUpdate={onUpdate}
        />
      </PageContainer>
  )
}
